import clsx from 'clsx';
import { useMutation } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Skeleton } from '@/components/feedback';
import { ShowingsAvailabilityCustomSlotForm } from '@/components/dashboard/showings/ShowingsAvailabilityCustomSlotForm';
import { ShowingsAvailabilityWeekTable } from '@/components/dashboard/showings/ShowingsAvailabilityWeekTable';
import {
  useShowingsAvailabilityWeek,
  type ShowingAvailabilityEditMode,
} from '@/components/dashboard/showings/useShowingsAvailabilityWeek';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { fetchShowingLeadId } from '@/lib/services/showings';

function EditModeButton({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={clsx(
        'min-h-11 rounded-2xl border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 disabled:cursor-not-allowed disabled:opacity-70',
        selected
          ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
          : 'border-surface-subtle bg-white text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/5 disabled:hover:border-surface-subtle disabled:hover:bg-white',
      )}
    >
      {label}
    </button>
  );
}

export function ShowingsAvailabilityManager(): JSX.Element {
  const user = useDashboardUser();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<ShowingAvailabilityEditMode>('toggle');
  const {
    weekStart,
    weekDates,
    customDate,
    customTime,
    slotMaps,
    timeRows,
    isLoading,
    isError,
    isUpdating,
    setWeekStartFromInput,
    shiftWeek,
    toggleSlot,
    setCustomDate,
    setCustomTime,
    addCustomSlotDisabled,
    addCustomSlot,
  } = useShowingsAvailabilityWeek();

  const openBookingMutation = useMutation({
    mutationFn: async (showingId: string) => {
      return await fetchShowingLeadId({ user, showingId });
    },
    onSuccess: (leadId) => {
      navigate(`/dashboard/leads/${leadId}`);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Unable to open showing details.');
      }
    },
  });

  return (
    <section className="rounded-2xl border border-surface-subtle bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-brand-primary">Showing availability</p>
          <p className="text-xs text-text-primary/60">
            Manage availability for the selected week. Choose an edit mode, then click cells (or
            shift-click for a range) to mark availability. Default is weekdays, 9:00am-5:00pm.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="min-h-11 rounded-full border border-surface-subtle px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:border-brand-primary/60 hover:bg-brand-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLoading || isUpdating}
            onClick={() => {
              shiftWeek(-7);
            }}
          >
            Prev
          </button>
          <label className="flex flex-col gap-1 text-xs text-text-primary/70">
            <span className="font-semibold uppercase tracking-wide">Week starting</span>
            <input
              type="date"
              value={weekStart}
              onChange={(event) => {
                setWeekStartFromInput(event.target.value);
              }}
              className="rounded-xl border border-surface-subtle bg-white px-3 py-2.5 text-base shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 md:py-2 md:text-sm"
            />
          </label>
          <button
            type="button"
            className="min-h-11 rounded-full border border-surface-subtle px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:border-brand-primary/60 hover:bg-brand-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLoading || isUpdating}
            onClick={() => {
              shiftWeek(7);
            }}
          >
            Next
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-xs text-text-primary/70">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-emerald-200 bg-emerald-50" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-surface-subtle bg-surface-subtle/40" />
              <span>Blocked</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm border border-amber-200 bg-amber-50" />
              <span>Booked (click for details)</span>
            </div>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              Edit mode
            </legend>
            <div className="grid grid-cols-3 gap-2">
              <EditModeButton
                label="Toggle"
                selected={editMode === 'toggle'}
                disabled={isUpdating}
                onClick={() => {
                  setEditMode('toggle');
                }}
              />
              <EditModeButton
                label="Mark available"
                selected={editMode === 'available'}
                disabled={isUpdating}
                onClick={() => {
                  setEditMode('available');
                }}
              />
              <EditModeButton
                label="Mark blocked"
                selected={editMode === 'blocked'}
                disabled={isUpdating}
                onClick={() => {
                  setEditMode('blocked');
                }}
              />
            </div>
          </fieldset>
        </div>

        {isLoading ? (
          <ShowingsAvailabilitySkeleton />
        ) : isError ? (
          <p className="text-sm font-semibold text-rose-600">Unable to load availability.</p>
        ) : (
          <ShowingsAvailabilityWeekTable
            weekDates={weekDates}
            timeRows={timeRows}
            slotMaps={slotMaps}
            isUpdating={isUpdating}
            onToggle={(date, time, shiftKey) => {
              toggleSlot(date, time, shiftKey, editMode);
            }}
            onOpenBooking={(showingId) => {
              openBookingMutation.mutate(showingId);
            }}
          />
        )}

        <ShowingsAvailabilityCustomSlotForm
          weekDates={weekDates}
          date={customDate}
          time={customTime}
          disabled={addCustomSlotDisabled}
          onDateChange={setCustomDate}
          onTimeChange={setCustomTime}
          onAdd={addCustomSlot}
        />
      </div>
    </section>
  );
}

function ShowingsAvailabilitySkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4" aria-label="Loading availability">
      <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
        <Skeleton className="h-10 w-full rounded-none" />
        <div className="divide-surface-muted divide-y">
          {Array.from({ length: 6 }).map((_row, index) => (
            <Skeleton
              key={`availability-row-${String(index)}`}
              className="h-12 w-full rounded-none"
            />
          ))}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Skeleton className="h-12 rounded-xl" />
        <Skeleton className="h-12 rounded-xl" />
        <Skeleton className="h-12 rounded-xl" />
      </div>
    </div>
  );
}
